import { useState } from 'react';
import SearchableSelect from '../SearchableSelect';
import PeriodMonthPicker from '../PeriodMonthPicker';
import { InlineLoader } from '../loading';
import { monthLabel } from './bannerOptions';
import { FIELD_INPUT, FieldLabel, PillRow } from './bannerFormBits';
import type { BannerDetails } from './useBannerDetails';
import { detailKey, detailLabel } from './useBannerDetails';
import type { ReachLevel, TargetMode } from './useReachLevel';

const MODES: { value: TargetMode; label: string; title: string }[] = [
  { value: 'fixed', label: 'One number', title: 'The same number for everyone.' },
  { value: 'own', label: 'Their own', title: 'Each person’s own number, from a detail that holds it.' },
  { value: 'per_value', label: 'Per detail', title: 'One number per value of any store or employee detail.' },
];

const BLANK_HINT = 'At or above the target counts as done. Leave a box blank for no target: its people are left out.';

/** More values than the two rows on show: a search finds the one wanted. */
const SEARCH_FROM = 8;

/** One box per value of the detail. A long list scrolls in its own box, so the form keeps its height. */
function PerValueBoxes({ level }: { level: ReachLevel }) {
  const { perField, perValues, boxValue, typeBox, addPerValue } = level;
  const [search, setSearch] = useState('');
  const [typed, setTyped] = useState('');
  if (!perField) return null;
  const q = search.trim().toLowerCase();
  const shown = q ? perValues.filter((v) => v.toLowerCase().includes(q)) : perValues;
  const addTyped = () => {
    addPerValue(typed);
    setTyped('');
  };
  return (
    <div className="flex flex-col gap-2">
      {perValues.length > SEARCH_FROM && (
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={`Search values of ${perField.label}`}
          placeholder="Search values…"
          className={FIELD_INPUT}
        />
      )}
      {shown.length > 0 ? (
        <div className="grid max-h-[118px] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
          {shown.map((value) => (
            <div key={value} className="min-w-0">
              <p className="mb-1 truncate text-xs font-medium text-gray-600" title={value}>
                {value}
              </p>
              <input
                type="text"
                inputMode="decimal"
                value={boxValue(value)}
                onChange={(e) => typeBox(value, e.target.value)}
                placeholder="—"
                aria-label={`Target for ${value}`}
                title="Leave blank for no target. Its people are left out."
                className={FIELD_INPUT}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500">No value matches.</p>
      )}
      {perField.valuesTruncated && (
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTyped();
            }
          }}
          onBlur={addTyped}
          aria-label={`Type a value of ${perField.label}`}
          placeholder={`First ${perField.values.length} listed. Type another, then Enter`}
          className={FIELD_INPUT}
        />
      )}
      {/* A phone has no hover, and this one changes who is sent it. */}
      <p className="text-xs text-gray-500 lg:hidden">Leave a box blank: its people are left out.</p>
    </div>
  );
}

/** Reach a level: what to check, and the number each person must reach. */
export function ReachLevelFields({
  level,
  details,
  audienceCounting = false,
}: {
  level: ReachLevel;
  details: BannerDetails;
  /** By details is still working out who the audience is. */
  audienceCounting?: boolean;
}) {
  const {
    sources,
    sourcesError,
    retrySources,
    picked,
    pickedKey,
    pickSource,
    sourceKey,
    month,
    pickMonth,
    mode,
    setMode,
    fixedValue,
    setFixedValue,
    ownKey,
    setOwnKey,
    perKey,
    pickPerField,
    perField,
    offersOwn,
    offersPer,
    hasValue,
    missing,
    preview,
    previewLoading,
    previewError,
    retryPreview,
    isCategoryScore,
    word,
    nameOfMonth,
  } = level;

  if (sourcesError) {
    return (
      <p className="text-xs text-red-600">
        {sourcesError}{' '}
        <button type="button" onClick={retrySources} className="font-medium text-primary-600 underline hover:text-primary-700">
          Try again
        </button>
      </p>
    );
  }
  if (!sources) return <InlineLoader label="Loading what can be checked…" />;

  return (
    <>
      <div className={`grid gap-3 ${picked?.needsMonth ? 'sm:grid-cols-[minmax(0,1fr)_10rem]' : ''}`}>
        <div className="min-w-0">
          <FieldLabel
            text="Check this"
            htmlFor="reach-level-source"
            hint="Anything this project keeps a number or a field for. A project with no scores lists none."
          />
          <SearchableSelect
            id="reach-level-source"
            value={pickedKey}
            onChange={pickSource}
            options={sources.sources.map((o) => ({ value: sourceKey(o), label: o.label, group: o.group }))}
            placeholder="Pick what to check…"
            searchPlaceholder="Search…"
            allowClear={false}
            emptyText={sources.sources.length === 0 ? 'This project has nothing to check yet' : 'No matches'}
          />
        </div>
        {picked?.needsMonth && (
          <PeriodMonthPicker
            label="Month"
            htmlFor="reach-level-month"
            value={month}
            onChange={(e) => pickMonth(e.target.value)}
            className="min-w-0"
            labelClassName="mb-1 block text-sm font-medium text-gray-700"
            inputClassName={`${FIELD_INPUT} h-[38px]`}
          />
        )}
      </div>

      {picked?.hasValueOnly ? (
        <div>
          <FieldLabel text="Must be" />
          <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">Has a value</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)]">
          <div className="min-w-0">
            <FieldLabel text="Must be" hint="At or above this number counts as done. Someone on several stores has the highest." />
            <PillRow
              small
              options={MODES.filter((m) => (m.value !== 'own' || offersOwn) && (m.value !== 'per_value' || offersPer))}
              value={mode}
              onChange={setMode}
            />
          </div>
          {mode === 'fixed' ? (
            <div className="min-w-0">
              <FieldLabel text="Target" htmlFor="reach-level-fixed" />
              {/* Text, not type="number": a number box reports text it cannot read as blank,
                  which would quietly mean "no target". Here it reaches the number check. */}
              <input
                id="reach-level-fixed"
                type="text"
                inputMode="decimal"
                value={fixedValue}
                onChange={(e) => setFixedValue(e.target.value)}
                placeholder="e.g. 80"
                className={FIELD_INPUT}
              />
            </div>
          ) : mode === 'own' ? (
            <div className="min-w-0">
              <FieldLabel text="Number in" htmlFor="reach-level-own" hint="The detail that holds each person's own number. Nobody with none is sent it." />
              <SearchableSelect
                id="reach-level-own"
                value={ownKey}
                onChange={setOwnKey}
                options={sources.ownTargets.map((o) => ({ value: detailKey(o), label: detailLabel(o) }))}
                placeholder="Pick a detail…"
                searchPlaceholder="Search details…"
                allowClear={false}
                emptyText="No detail holds a number"
              />
            </div>
          ) : (
            <div className="min-w-0">
              <FieldLabel text="Number per" htmlFor="reach-level-per" hint={BLANK_HINT} />
              {details.error ? (
                <p className="text-xs text-red-600">
                  {details.error}{' '}
                  <button type="button" onClick={details.retry} className="font-medium text-primary-600 underline hover:text-primary-700">
                    Try again
                  </button>
                </p>
              ) : (
                <SearchableSelect
                  id="reach-level-per"
                  value={perKey}
                  onChange={pickPerField}
                  options={details.targetFieldOptions}
                  placeholder={details.loaded ? 'Pick a detail…' : 'Loading…'}
                  searchPlaceholder="Search details…"
                  allowClear={false}
                  emptyText="This project has no details to pick from"
                />
              )}
            </div>
          )}
        </div>
      )}

      {!picked?.hasValueOnly && mode === 'per_value' && perField && <PerValueBoxes key={perKey} level={level} />}

      {/* Where this audience stands on the target before anything is sent, beside the
          targets that move it. The third tile's people are left out of the send altogether. */}
      <div aria-live="polite" aria-busy={previewLoading}>
        {previewError ? (
          <p className="text-xs text-red-600">
            {previewError}{' '}
            <button type="button" onClick={retryPreview} className="font-medium text-primary-600 underline hover:text-primary-700">
              Try again
            </button>
          </p>
        ) : preview ? (
          <div className={`grid ${hasValue ? 'grid-cols-2' : 'grid-cols-3'} gap-2 transition-opacity ${previewLoading ? 'opacity-50' : ''}`}>
            {(hasValue
              ? [
                  {
                    label: 'Not filled in',
                    value: preview.belowTarget,
                    note: null,
                    hint: 'They get the banner, and a push if it is on.',
                    tone: 'text-amber-800 bg-amber-50 border-amber-200',
                  },
                  {
                    label: 'Filled in',
                    value: preview.alreadyMet,
                    note: null,
                    hint: 'Written as done, with no push.',
                    tone: 'text-green-700 bg-green-50 border-green-200',
                  },
                ]
              : [
                  {
                    label: 'Below target',
                    value: preview.belowTarget,
                    note: preview.noFigure > 0 ? `${preview.noFigure.toLocaleString()} with no ${word} yet` : null,
                    hint:
                      preview.noFigure > 0
                        ? `${preview.noFigure} of them have no ${word} yet${monthLabel(nameOfMonth) ? ` for ${monthLabel(nameOfMonth)}` : ''}.`
                        : 'They get the banner, and a push if it is on.',
                    tone: 'text-amber-800 bg-amber-50 border-amber-200',
                  },
                  {
                    label: 'Already met',
                    value: preview.alreadyMet,
                    note: null,
                    hint: `Written as done, with no push. It shows if their ${word} drops below.`,
                    tone: 'text-green-700 bg-green-50 border-green-200',
                  },
                  {
                    label: isCategoryScore ? 'No category' : 'No target',
                    value: preview.noTarget,
                    note: null,
                    hint: isCategoryScore
                      ? 'None of their Active stores has a category with a target, so they are not sent it.'
                      : 'Nothing gives them a target, so they are not sent it.',
                    tone: 'text-gray-700 bg-white border-gray-200',
                  },
                ]
            ).map((tile) => (
              // The explanation is on hover, and read out with the number.
              <div key={tile.label} title={tile.hint} className={`rounded-md border px-2 py-1.5 text-center ${tile.tone}`}>
                <div className="text-base font-semibold leading-tight">{tile.value.toLocaleString()}</div>
                <div className="text-[10px] uppercase tracking-wide opacity-70">{tile.label}</div>
                {tile.note && <div className="text-[10px] opacity-80">{tile.note}</div>}
                <span className="sr-only">{tile.hint}</span>
              </div>
            ))}
          </div>
        ) : previewLoading || audienceCounting ? (
          <InlineLoader label="Counting…" />
        ) : (
          !missing && (
            <p className="text-xs text-gray-500">
              {hasValue ? 'Pick who to send it to, to see who has filled it in.' : 'Pick who to send it to, to see who is below target.'}
            </p>
          )
        )}
      </div>
    </>
  );
}
